// Schedules as people read them (M1-05). Parsing, normalising and occurrence maths are
// `@companyos/domain-work/recurrence` (the API's own code); this file only turns its `Rule` into
// a sentence ("Weekly on Mon at 09:00", "Monthly on the first Mon at 10:00"), builds the rule
// the seat drawer's schedule builder describes, and formats an instant in the company's zone.
import {
  formatRule,
  nextAfter,
  parseRule,
  type Rule,
  type Weekday,
} from "@companyos/domain-work/recurrence";

export type { Rule, Weekday };

/** Monday … Sunday, ISO numbered (the package's `Weekday`). */
export const WEEKDAYS: readonly Weekday[] = [1, 2, 3, 4, 5, 6, 7];
export const WEEKDAY_SHORT: Record<Weekday, string> = {
  1: "Mon",
  2: "Tue",
  3: "Wed",
  4: "Thu",
  5: "Fri",
  6: "Sat",
  7: "Sun",
};
export const WEEKDAY_LONG: Record<Weekday, string> = {
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
  7: "Sunday",
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const ORDINAL: Record<string, string> = {
  "1": "first",
  "2": "second",
  "3": "third",
  "4": "fourth",
  "5": "fifth",
  "-1": "last",
  "-2": "second-to-last",
};

const pad = (n: number) => String(n).padStart(2, "0");
const list = (items: string[]) =>
  items.length <= 1
    ? (items[0] ?? "")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
const nth = (n: number) => ORDINAL[String(n)] ?? `#${n}`;

function monthDay(n: number): string {
  if (n === -1) return "the last day";
  if (n < 0) return `the ${nth(n)} day`;
  return `day ${n}`;
}

/** The rule as a sentence: "Every 2 weeks on Mon and Thu at 09:30". */
export function describeRule(rule: Rule): string {
  const unit = {
    DAILY: ["Daily", "days"],
    WEEKLY: ["Weekly", "weeks"],
    MONTHLY: ["Monthly", "months"],
    YEARLY: ["Yearly", "years"],
  } as const;
  const [once, many] = unit[rule.freq];
  let text = rule.interval === 1 ? once : `Every ${rule.interval} ${many}`;
  if (rule.byMonth.length) text += ` in ${list(rule.byMonth.map((m) => MONTHS[m - 1] ?? `${m}`))}`;
  if (rule.byMonthDay.length)
    text += ` on ${rule.byMonthDay.length === 1 ? monthDay(rule.byMonthDay[0] ?? 1) : `days ${list(rule.byMonthDay.map(String))}`}`;
  if (rule.byDay.length) {
    const plain = rule.byDay.filter((d) => d.n === 0).map((d) => d.weekday);
    const weekdays =
      plain.length === 5 && [1, 2, 3, 4, 5].every((d) => plain.includes(d as Weekday));
    const parts = [
      ...(weekdays ? ["weekdays"] : plain.map((d) => WEEKDAY_SHORT[d])),
      ...rule.byDay
        .filter((d) => d.n !== 0)
        .map((d) => `the ${nth(d.n)} ${WEEKDAY_SHORT[d.weekday]}`),
    ];
    text += `${rule.byMonthDay.length ? " if a" : " on"} ${list(parts)}`;
  }
  if (rule.bySetPos.length) text += ` (occurrence ${list(rule.bySetPos.map(nth))} of each period)`;
  const times = rule.byHour.flatMap((h) => rule.byMinute.map((m) => `${pad(h)}:${pad(m)}`));
  text += ` at ${list(times)}`;
  if (rule.count !== null) text += `, ${rule.count} times`;
  if (rule.until !== null) text += `, until ${rule.until.toISOString().slice(0, 10)}`;
  return text;
}

/** A schedule's text as a sentence, or the text itself when it does not parse. */
export function describeSchedule(text: string): string {
  const parsed = parseRule(text);
  return parsed.ok ? describeRule(parsed.rule) : text;
}

/** What the schedule builder offers: daily / weekly / monthly at one time of day. */
export type Builder = {
  freq: "DAILY" | "WEEKLY" | "MONTHLY";
  interval: number;
  days: Weekday[];
  monthDay: number;
  hour: number;
  minute: number;
};

/** The builder's rule, as the API will store it (`formatRule`'s normalised text). */
export function builderRule(b: Builder): Rule {
  return {
    freq: b.freq,
    interval: b.interval,
    byDay:
      b.freq === "WEEKLY"
        ? WEEKDAYS.filter((d) => b.days.includes(d)).map((weekday) => ({ weekday, n: 0 }))
        : [],
    byMonthDay: b.freq === "MONTHLY" ? [b.monthDay] : [],
    byMonth: [],
    byHour: [b.hour],
    byMinute: [b.minute],
    bySetPos: [],
    wkst: 1,
    count: null,
    until: null,
  };
}

export const builderText = (b: Builder) => formatRule(builderRule(b));

/** The first run of a rule saved now (the anchor is the save time, like `created_at`). */
export function firstRun(rule: Rule, tz: string, now = new Date()): Date | null {
  try {
    return nextAfter(rule, now, tz, now);
  } catch {
    return nextAfter(rule, now, "UTC", now);
  }
}

function zoneOf(tz: string): string {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return tz;
  } catch {
    return "UTC";
  }
}

/** "Mon, Oct 26, 09:00 GMT": an instant as wall-clock time in the company's time zone. */
export function formatInZone(iso: string | Date, tz: string): string {
  const t = typeof iso === "string" ? new Date(iso) : iso;
  return t.toLocaleString("en-US", {
    timeZone: zoneOf(tz),
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZoneName: "short",
  });
}
